/**
 * Avatar creation flows for V2.
 *
 *  - AiAvatarModal   : Prompt-to-Avatar (`type: "prompt"`). Mirrors
 *                      CreatePromptAvatarRequest: required fields are ONLY
 *                      `name` + `prompt` (max 1000 chars) — everything else
 *                      here is an OPTIONAL prompt-building aid (the API has no
 *                      structured appearance fields), plus `reference_images`
 *                      (up to 3) which the API does accept directly.
 *  - PhotoAvatarModal: Photo Avatar (`type: "photo"`). One photo + name,
 *                      gated on Vivideo's legal consent (real-person likeness).
 *  - DigitalTwinModal: Digital Twin (`type: "digital_twin"`). A short training
 *                      video of a real person + name, gated on consent. The
 *                      video is uploaded via the asset pipeline (HeyGen
 *                      asset_id) and HeyGen then requires a browser consent
 *                      approval, surfaced on the avatar card under "My avatars".
 *
 * All portal to document.body (`.glass` ancestors use backdrop-filter, which
 * traps fixed overlays) and call v2Service.createAvatar.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { trackAmpAvatarCreated } from '@/services/amplitudeService';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Wand2, Camera, Loader2, Sparkles, Upload, ImagePlus, Video, ShieldCheck, Zap, MoveHorizontal, RotateCcw } from 'lucide-react';
import { cn } from '@/v2/utils';
import { v2Service } from '@/api/v2Service';
import { isFreeLimitError, isCreditDeclined, useUpgradeModal } from '@/v2/upgradeModal';
import { apiService } from '@/api/apiService';
import { useUserStore } from '@/store/userStore';
import { ConsentNotice } from '@/v2/components/ConsentNotice';
import { toast } from '@/v2/toast';
import i18n from '@/i18n';
import type { V2CreateAvatarResult } from '@/api/v2Types';

// The create-avatar API takes only `name` + a free-text `prompt` (≤1000 chars)
// + optional reference_images — so the UI is exactly that: describe your
// avatar in plain words, optionally tap a few boosters, generate.
const SAMPLE_APPEARANCE = () =>
  i18n.t('avatarCreate.sampleAppearance', { ns: 'v2' });

// Optional one-tap phrases appended to the description (multi-select).
const BOOSTERS = () => [
  i18n.t('avatarCreate.boosterPhotorealistic', { ns: 'v2' }),
  i18n.t('avatarCreate.boosterCinematicLighting', { ns: 'v2' }),
  i18n.t('avatarCreate.boosterStudioBackdrop', { ns: 'v2' }),
  i18n.t('avatarCreate.boosterOutdoorSetting', { ns: 'v2' }),
  i18n.t('avatarCreate.boosterProfessionalLook', { ns: 'v2' }),
  i18n.t('avatarCreate.boosterCasualLook', { ns: 'v2' }),
  i18n.t('avatarCreate.boosterWarmSmile', { ns: 'v2' }),
  i18n.t('avatarCreate.boosterUpperBodyFraming', { ns: 'v2' }),
];

const MAX_PROMPT = 1000;
const MAX_REF_IMAGES = 3;

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50 focus:bg-white/[0.07] transition-colors';

// Portaled to document.body — `.glass` ancestors use backdrop-filter, which
// would otherwise trap this fixed overlay inside their stacking context.
function ModalShell({ title, subtitle, icon: Icon, onClose, wide, children }: {
  title: string; subtitle?: string; icon: typeof Wand2; onClose: () => void; wide?: boolean; children: React.ReactNode;
}) {
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in" onClick={onClose}>
      <div
        className={cn(
          'w-full max-h-[92vh] flex flex-col rounded-3xl bg-[#15151d] border border-white/10 shadow-2xl animate-slide-up overflow-hidden',
          wide ? 'max-w-3xl' : 'max-w-xl'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-7 py-5 border-b border-white/10 flex items-center gap-4">
          <span className="w-11 h-11 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-primary" /></span>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-text-primary tracking-tight">{title}</h2>
            {subtitle && <p className="text-xs text-text-tertiary mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6">{children}</div>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-sm font-semibold text-text-primary">{label}</label>
        {hint && <span className="text-[11px] text-text-tertiary">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI (prompt-to-avatar) — radically simple: name, describe, generate.
// ---------------------------------------------------------------------------

export function AiAvatarModal({ onClose, onCreated }: { onClose: () => void; onCreated?: (r: V2CreateAvatarResult) => void }) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const isPaid = useUserStore((s) => s.isPro());
  const [name, setName] = useState('');
  const [describe, setDescribe] = useState('');
  const [boosters, setBoosters] = useState<string[]>([]);
  const [refImages, setRefImages] = useState<string[]>([]);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const prompt = useMemo(() => {
    const parts = [describe.trim(), ...boosters];
    return parts.filter(Boolean).join(', ').slice(0, MAX_PROMPT);
  }, [describe, boosters]);

  const toggleBooster = (b: string) =>
    setBoosters((prev) => (prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]));

  const addRefImages = async (files: FileList | null) => {
    if (!files?.length || !userId) return;
    setUploadingRef(true);
    try {
      for (const f of Array.from(files).slice(0, MAX_REF_IMAGES - refImages.length)) {
        const url = await apiService.uploadImage(userId, f, f.type || 'image/jpeg');
        setRefImages((prev) => (prev.length < MAX_REF_IMAGES ? [...prev, url] : prev));
      }
    } catch {
      setErr(t('avatarCreate.refImageUploadFailed'));
    } finally { setUploadingRef(false); }
  };

  const create = async () => {
    if (!userId || !name.trim() || !prompt.trim() || busy) return;
    // Premium gating: creating an avatar is paid-only for free users → redirect
    // to upgrade at the CTA (no API call, no cost).
    if (!isPaid) { useUpgradeModal.getState().show({ kind: 'free_asset_limit', asset: 'avatar' }); return; }
    setBusy(true); setErr(null);
    try {
      const r = await v2Service.createAvatar({
        userId, kind: 'prompt', name: name.trim(), prompt,
        referenceImages: refImages.length ? refImages : undefined,
      });
      trackAmpAvatarCreated({ kind: 'prompt' });
      onCreated?.(r);
      onClose();
      toast.info(t('avatarCreate.creatingAvatarToast'));
    } catch (e) {
      if (isFreeLimitError(e) || isCreditDeclined(e)) { onClose(); return; } // popup / user cancel
      setErr(t('avatarCreate.couldNotStartGeneration'));
      toast.error(t('avatarCreate.couldNotStartGenerationToast'));
    } finally { setBusy(false); }
  };

  return (
    <ModalShell
      title={t('avatarCreate.aiModalTitle')}
      subtitle={t('avatarCreate.aiModalSubtitle')}
      icon={Wand2}
      onClose={onClose}
    >
      <Field label={t('avatarCreate.nameLabel')} hint={t('avatarCreate.requiredHint')}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('avatarCreate.aiNamePlaceholder')} className={inputCls} />
      </Field>

      <Field label={t('avatarCreate.describeAvatarLabel')} hint={`${prompt.length}/${MAX_PROMPT}`}>
        <textarea
          rows={5}
          value={describe}
          onChange={(e) => setDescribe(e.target.value)}
          placeholder={t('avatarCreate.describePlaceholder')}
          className={cn(inputCls, 'resize-none')}
        />
        <button type="button" onClick={() => setDescribe(SAMPLE_APPEARANCE())} className="mt-1.5 text-xs text-primary/90 hover:text-primary font-medium">
          {t('avatarCreate.trySample')}
        </button>
      </Field>

      {/* Optional one-tap boosters — appended to the description */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm font-semibold text-text-primary">{t('avatarCreate.quickTouches')}</span>
          <span className="text-[11px] text-text-tertiary">{t('avatarCreate.optionalTapToAdd')}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {BOOSTERS().map((b) => (
            <button key={b} type="button" onClick={() => toggleBooster(b)}
              className={cn('px-3 py-1.5 rounded-full text-xs font-semibold transition-colors',
                boosters.includes(b)
                  ? 'bg-primary/15 border border-primary/40 text-primary'
                  : 'bg-white/5 border border-transparent text-text-secondary hover:text-text-primary hover:bg-white/10')}>
              {b}
            </button>
          ))}
        </div>
      </div>

      {/* Optional reference images (per the create-avatar API) */}
      <Field label={t('avatarCreate.referenceImagesLabel')} hint={t('avatarCreate.referenceImagesHint')}>
        <div className="flex gap-3 flex-wrap">
          {refImages.map((url, i) => (
            <div key={url} className="relative w-16 h-16 rounded-xl overflow-hidden border border-white/10 group">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button onClick={() => setRefImages((prev) => prev.filter((_, j) => j !== i))}
                className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          {refImages.length < MAX_REF_IMAGES && (
            <label className="w-16 h-16 rounded-xl border-2 border-dashed border-white/10 hover:border-primary/50 flex items-center justify-center text-text-tertiary hover:text-primary cursor-pointer transition-colors">
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => void addRefImages(e.target.files)} />
              {uploadingRef ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            </label>
          )}
        </div>
      </Field>

      {err && <p className="text-xs text-amber-300/80">{err}</p>}
      <button onClick={() => void create()} disabled={busy || !name.trim() || !prompt.trim()}
        className="w-full py-3 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-95 hover:shadow-glow disabled:opacity-50 transition-all">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {busy ? t('avatarCreate.generating') : t('avatarCreate.generateAvatar')}
      </button>
      <p className="text-[11px] text-text-tertiary text-center">{t('avatarCreate.takesAFewMinutesBefore')}<span className="text-text-secondary">{t('avatarCreate.myAvatars')}</span>{t('avatarCreate.takesAFewMinutesAfter')}</p>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Photo avatar
// ---------------------------------------------------------------------------

export function PhotoAvatarModal({ onClose, onCreated }: { onClose: () => void; onCreated?: (r: V2CreateAvatarResult) => void }) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const isPaid = useUserStore((s) => s.isPro());
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pick = (f: File | null) => {
    setFile(f);
    setPreviewUrl(f ? URL.createObjectURL(f) : null);
  };

  const create = async () => {
    if (!userId || !name.trim() || !file || !consent || busy) return;
    // Premium gating: converting a photo to an avatar is paid-only → redirect
    // free users to upgrade at the CTA (no upload, no API call).
    if (!isPaid) { useUpgradeModal.getState().show({ kind: 'free_asset_limit', asset: 'avatar' }); return; }
    setBusy(true); setErr(null);
    try {
      const url = await apiService.uploadImage(userId, file, file.type || 'image/jpeg');
      const r = await v2Service.createAvatar({ userId, kind: 'photo', name: name.trim(), fileUrl: url, consent: true });
      trackAmpAvatarCreated({ kind: 'photo' });
      onCreated?.(r);
      onClose();
      toast.info(t('avatarCreate.creatingAvatarToast'));
    } catch (e) {
      if (isFreeLimitError(e) || isCreditDeclined(e)) { onClose(); return; } // popup / user cancel
      setErr(t('avatarCreate.couldNotCreateAvatar'));
      toast.error(t('avatarCreate.couldNotCreateAvatarToast'));
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={t('avatarCreate.photoModalTitle')} icon={Camera} onClose={onClose}>
      <Field label={t('avatarCreate.nameLabel')} hint={t('avatarCreate.requiredHint')}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('avatarCreate.photoNamePlaceholder')} className={inputCls} />
      </Field>

      <Field label={t('avatarCreate.photoLabel')} hint={t('avatarCreate.photoHint')}>
        <label className="block">
          <input type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0] ?? null)} />
          {previewUrl ? (
            <div className="relative w-full aspect-[3/4] max-w-[220px] mx-auto rounded-2xl overflow-hidden border border-white/10 cursor-pointer">
              <img src={previewUrl} alt="" className="w-full h-full object-cover" />
              <span className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center text-white text-xs transition-opacity">{t('avatarCreate.changePhoto')}</span>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 py-10 rounded-2xl border-2 border-dashed border-white/10 text-text-tertiary hover:border-primary/40 cursor-pointer transition-colors">
              <Upload className="w-6 h-6" />
              <span className="text-sm">{t('avatarCreate.uploadFrontFacingPhoto')}</span>
            </div>
          )}
        </label>
      </Field>

      <ConsentNotice checked={consent} onChange={setConsent} />

      {err && <p className="text-xs text-amber-300/80">{err}</p>}
      <button onClick={() => void create()} disabled={busy || !name.trim() || !file || !consent}
        className="w-full py-3 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-95 disabled:opacity-50 transition-opacity">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        {busy ? t('avatarCreate.creating') : t('avatarCreate.createAvatar')}
      </button>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Digital twin — a short training VIDEO of a real person (type: digital_twin).
// The video goes through the asset pipeline (returns a HeyGen asset_id), then
// HeyGen requires a browser consent approval, surfaced on the avatar card.
// ---------------------------------------------------------------------------

// HeyGen doesn't publish hard limits; these are safe, generous client-side
// guards (the asset backend caps at 500MB). Guidance below is general so we
// don't over-claim undocumented specs.
const MAX_VIDEO_BYTES = 500 * 1024 * 1024;

// Shown BEFORE recording starts — the two rules that most affect twin quality.
const TWIN_RULES = (): { icon: typeof Zap; title: string; detail: string }[] => [
  { icon: Zap, title: i18n.t('avatarCreate.ruleEnergyTitle', { ns: 'v2' }), detail: i18n.t('avatarCreate.ruleEnergyDetail', { ns: 'v2' }) },
  { icon: MoveHorizontal, title: i18n.t('avatarCreate.ruleStableTitle', { ns: 'v2' }), detail: i18n.t('avatarCreate.ruleStableDetail', { ns: 'v2' }) },
];

type TwinRec = 'choose' | 'prep' | 'countdown' | 'recording' | 'recorded';

export function DigitalTwinModal({ onClose, onCreated }: { onClose: () => void; onCreated?: (r: V2CreateAvatarResult) => void }) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const isPaid = useUserStore((s) => s.isPro());
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // On-site recording (camera + mic), mirrors the voice-clone flow.
  const [rec, setRec] = useState<TwinRec>('choose');
  const [countdown, setCountdown] = useState(3);
  const [recSecs, setRecSecs] = useState(0);
  const [recBlobUrl, setRecBlobUrl] = useState<string | null>(null);
  // A liveness/safety code the user reads aloud — fixed for this session.
  const [safetyCode] = useState(() => Math.floor(Math.random() * 90) + 10); // 10–99
  const liveRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recBlobRef = useRef<Blob | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const SCRIPT = t('avatarCreate.recordScript', { code: safetyCode });

  const stopTracks = () => streamRef.current?.getTracks().forEach((t) => t.stop());

  // cleanup on close: stop camera + timer
  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    stopTracks();
  }, []);

  // Attach the live stream once the <video> for the active camera state mounts.
  useEffect(() => {
    if ((rec === 'prep' || rec === 'countdown' || rec === 'recording') && liveRef.current && streamRef.current) {
      liveRef.current.srcObject = streamRef.current;
      void liveRef.current.play().catch(() => undefined);
    }
  }, [rec]);

  const pick = (f: File | null) => {
    setErr(null);
    if (f && f.size > MAX_VIDEO_BYTES) {
      setErr(t('avatarCreate.videoTooLarge'));
      return;
    }
    setFile(f);
    setPreviewUrl(f ? URL.createObjectURL(f) : null);
  };

  const clearVideo = () => {
    setFile(null);
    setPreviewUrl(null);
    setRec('choose');
  };

  const startCamera = async () => {
    setErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true });
      streamRef.current = stream;
      setRec('prep');
    } catch {
      setRec('choose');
      setErr(t('avatarCreate.cameraBlocked'));
    }
  };

  const cancelCamera = () => { stopTracks(); setRec('choose'); };

  const beginRecording = async () => {
    const stream = streamRef.current;
    if (!stream) return;
    setRec('countdown');
    for (const n of [3, 2, 1]) { setCountdown(n); await new Promise((r) => setTimeout(r, 1000)); }
    // Prefer mp4 (Safari) over webm (Chrome) for the widest training-input support.
    const mime = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4'
      : MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : '';
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    chunksRef.current = [];
    recorder.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'video/webm' });
      recBlobRef.current = blob;
      setRecBlobUrl(URL.createObjectURL(blob));
      setRec('recorded');
      stopTracks();
    };
    recorderRef.current = recorder;
    recorder.start();
    setRecSecs(0);
    setRec('recording');
    timerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    recorderRef.current?.stop(); // onstop builds the blob + flips to 'recorded'
  };

  const retake = async () => {
    if (recBlobUrl) URL.revokeObjectURL(recBlobUrl);
    recBlobRef.current = null;
    setRecBlobUrl(null);
    setRecSecs(0);
    await startCamera();
  };

  const useRecording = () => {
    const blob = recBlobRef.current;
    if (!blob) return;
    if (blob.size > MAX_VIDEO_BYTES) { setErr(t('avatarCreate.recordingTooLarge')); return; }
    const ext = (blob.type || '').includes('mp4') ? 'mp4' : 'webm';
    const f = new File([blob], `digital-twin-${name.trim() || 'recording'}.${ext}`, { type: blob.type || 'video/webm' });
    setFile(f);
    setPreviewUrl(recBlobUrl); // reuse the recorded object URL for the preview
    setRec('choose');          // file present → form view shows the preview
  };

  const create = async () => {
    if (!userId || !name.trim() || !file || !consent || busy) return;
    // Premium gating: creating a digital twin is paid-only → redirect free users
    // to upgrade at the CTA (no upload, no API call).
    if (!isPaid) { useUpgradeModal.getState().show({ kind: 'free_asset_limit', asset: 'avatar' }); return; }
    setBusy(true); setErr(null); setUploadPct(0);
    try {
      // Video → asset pipeline (presigned S3, up to 500MB) → HeyGen asset_id.
      const asset = await v2Service.uploadAsset(userId, file, (p) => setUploadPct(p));
      const r = await v2Service.createAvatar({
        userId, kind: 'digital_twin', name: name.trim(), assetId: asset.asset_id, consent: true,
      });
      trackAmpAvatarCreated({ kind: 'digital_twin' });
      onCreated?.(r);
      onClose();
      toast.info(t('avatarCreate.buildingTwinToast'));
    } catch (e) {
      if (isFreeLimitError(e) || isCreditDeclined(e)) { onClose(); return; } // popup / user cancel
      setErr(t('avatarCreate.couldNotCreateTwin'));
      toast.error(t('avatarCreate.couldNotCreateTwinToast'));
    } finally { setBusy(false); setUploadPct(null); }
  };

  const mmss = `${String(Math.floor(recSecs / 60)).padStart(2, '0')}:${String(recSecs % 60).padStart(2, '0')}`;

  // The read-aloud script panel (teleprompter). `big` = recording view.
  const ScriptCard = ({ big }: { big?: boolean }) => (
    <div className="rounded-2xl border border-primary/25 bg-primary/[0.06] p-5">
      <p className="text-[11px] font-bold uppercase tracking-widest text-primary mb-2">{t('avatarCreate.readOutLoud')}</p>
      <p className={cn('text-text-primary whitespace-pre-line', big ? 'text-xl leading-relaxed' : 'text-base leading-relaxed')}>
        {SCRIPT}
      </p>
      <p className="mt-3 inline-flex items-center gap-2 text-xs text-text-secondary">
        <ShieldCheck className="w-3.5 h-3.5 text-primary" />
        {t('avatarCreate.saySafetyCode')} <span className="font-bold text-text-primary tabular-nums">{safetyCode}</span>
      </p>
    </div>
  );

  // ----- Camera flow (prep / countdown / recording / recorded) -----
  const cameraView = (
    <div className="space-y-5">
      {rec === 'prep' && (
        <>
          {/* The guideline shown BEFORE recording starts */}
          <div className="grid sm:grid-cols-2 gap-3">
            {TWIN_RULES().map((r) => (
              <div key={r.title} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <span className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center shrink-0"><r.icon className="w-4 h-4 text-primary" /></span>
                <div>
                  <p className="text-sm font-semibold text-text-primary">{r.title}</p>
                  <p className="text-xs text-text-tertiary mt-0.5 leading-relaxed">{r.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="relative rounded-2xl overflow-hidden bg-black aspect-video max-w-2xl mx-auto border border-white/10">
            <video ref={liveRef} autoPlay muted playsInline className="w-full h-full object-cover -scale-x-100" />
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-[11px] text-white">{t('avatarCreate.frameYourself')}</span>
          </div>

          <ScriptCard />

          <div className="flex gap-2">
            <button onClick={() => void beginRecording()}
              className="flex-1 py-3 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-95 transition-opacity flex items-center justify-center gap-2">
              <Video className="w-4 h-4" /> {t('avatarCreate.imReadyStartRecording')}
            </button>
            <button onClick={cancelCamera} className="px-5 py-3 rounded-full bg-white/5 border border-white/10 text-sm text-text-secondary hover:text-text-primary">{t('avatarCreate.back')}</button>
          </div>
        </>
      )}

      {rec === 'countdown' && (
        <div className="relative rounded-2xl overflow-hidden bg-black aspect-video max-w-2xl mx-auto border border-primary/40">
          <video ref={liveRef} autoPlay muted playsInline className="w-full h-full object-cover -scale-x-100" />
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30">
            <p className="text-7xl font-extrabold text-white tabular-nums drop-shadow-lg animate-pulse">{countdown}</p>
            <p className="text-xs text-white/80 mt-2">{t('avatarCreate.getReadySpeakWithEnergy')}</p>
          </div>
        </div>
      )}

      {rec === 'recording' && (
        <>
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-video max-w-2xl mx-auto border border-red-400/50">
            <video ref={liveRef} autoPlay muted playsInline className="w-full h-full object-cover -scale-x-100" />
            <span className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1 rounded-full bg-black/60 text-xs font-semibold text-white">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> {t('avatarCreate.recIndicator')} {mmss}
            </span>
          </div>
          <ScriptCard big />
          <div className="text-center">
            <button onClick={stopRecording} className="px-6 py-3 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-400 transition-colors">
              {t('avatarCreate.stopRecording')}
            </button>
          </div>
        </>
      )}

      {rec === 'recorded' && recBlobUrl && (
        <>
          <div className="rounded-2xl overflow-hidden bg-black aspect-video max-w-2xl mx-auto border border-white/10">
            <video src={recBlobUrl} controls playsInline className="w-full h-full object-contain" />
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-text-secondary">
            <span className="font-semibold text-text-primary">{t('avatarCreate.yourRecording')}</span> · {mmss}
            {recSecs < 8 && <span className="text-amber-300/80">{t('avatarCreate.fewSecondsLongerTrainsBetter')}</span>}
          </div>
          <div className="flex gap-2 max-w-md mx-auto">
            <button onClick={useRecording}
              className="flex-1 py-3 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold hover:opacity-95 transition-opacity">
              {t('avatarCreate.useThisRecording')}
            </button>
            <button onClick={() => void retake()} className="px-5 py-3 rounded-full bg-white/5 border border-white/10 text-sm text-text-secondary hover:text-text-primary flex items-center gap-2">
              <RotateCcw className="w-4 h-4" /> {t('avatarCreate.retake')}
            </button>
          </div>
        </>
      )}

      {err && <p className="text-xs text-amber-300/80 text-center">{err}</p>}
    </div>
  );

  // ----- Form view (choose source / preview the chosen video → name + consent) -----
  const formView = (
    <>
      <Field label={t('avatarCreate.nameLabel')} hint={t('avatarCreate.requiredHint')}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('avatarCreate.twinNamePlaceholder')} className={inputCls} />
      </Field>

      <Field label={t('avatarCreate.trainingVideoLabel')} hint={t('avatarCreate.trainingVideoHint')}>
        {previewUrl ? (
          <div className="space-y-2">
            <div className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden border border-white/10 bg-black">
              <video src={previewUrl} className="w-full aspect-video object-contain" controls playsInline />
            </div>
            <button onClick={clearVideo} className="mx-auto block text-xs text-text-tertiary hover:text-text-primary">{t('avatarCreate.useDifferentVideo')}</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              onClick={() => void startCamera()}
              className="flex flex-col items-center justify-center gap-2 py-8 rounded-2xl border-2 border-dashed border-white/10 text-text-tertiary hover:border-primary/50 hover:text-primary transition-colors"
            >
              <Camera className="w-6 h-6" />
              <span className="text-sm font-semibold">{t('avatarCreate.recordNow')}</span>
              <span className="text-[11px]">{t('avatarCreate.wellShowScript')}</span>
            </button>
            <label className="flex flex-col items-center justify-center gap-2 py-8 rounded-2xl border-2 border-dashed border-white/10 text-text-tertiary hover:border-primary/50 hover:text-primary cursor-pointer transition-colors">
              <input type="file" accept="video/*" className="hidden" onChange={(e) => pick(e.target.files?.[0] ?? null)} />
              <Upload className="w-6 h-6" />
              <span className="text-sm font-semibold">{t('avatarCreate.uploadAFile')}</span>
              <span className="text-[11px]">{t('avatarCreate.mp4OrMovUpTo500')}</span>
            </label>
          </div>
        )}
      </Field>

      <ConsentNotice checked={consent} onChange={setConsent} />

      <p className="flex items-start gap-2 text-[11px] text-text-tertiary leading-relaxed">
        <ShieldCheck className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
        {t('avatarCreate.consentNoticeBefore')}
        <span className="text-text-secondary"> {t('avatarCreate.consentButtonWord')} </span>{t('avatarCreate.consentNoticeAfter')}
      </p>

      {err && <p className="text-xs text-amber-300/80">{err}</p>}
      <button onClick={() => void create()} disabled={busy || !name.trim() || !file || !consent}
        className="w-full py-3 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-95 disabled:opacity-50 transition-opacity">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
        {busy
          ? uploadPct !== null && uploadPct < 100 ? t('avatarCreate.uploadingPct', { pct: uploadPct }) : t('avatarCreate.creating')
          : t('avatarCreate.createDigitalTwin')}
      </button>
    </>
  );

  const inCamera = rec !== 'choose' && !file;

  return (
    <ModalShell
      title={t('avatarCreate.twinModalTitle')}
      subtitle={t('avatarCreate.twinModalSubtitle')}
      icon={Video}
      onClose={onClose}
      wide
    >
      {inCamera ? cameraView : formView}
    </ModalShell>
  );
}
